import { PageContent, PageHeader } from '@/components';
import { MasterTableResourceGuard } from '@/features/authentication/_components/MasterTableResourceGuard';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router';
import {
  AccountTitlesPage,
  AccountingTypesPage,
  BudgetTypesPage,
  CostsPage,
  CreateMasterTableButton,
  DrawerStateProvider,
  ExpenditureBudgetJurisdictionsPage,
  ExpenditureBudgetOrganizationsPage,
  ExpenditureBudgetPurposesPage,
  ExpenditureItemMajorMattersPage,
  ExpenditureItemNaturePresetsPage,
  ExpenditureNaturesPage,
  ExpenditurePurposeOfSettlementStatisticsPresetsPage,
  ExpenditurePurposesPage,
  IndicatorMeasurementsPage,
  MajorMatterPage,
  MasterTableListPage,
  MinistriesPage,
  RevenueNaturesPage,
} from './components';
import { PolicyRoutes } from './policy/routes';
import ReservedProjectsPage from './reserved-project/_pages/ReservedProjectsPage';

export const MasterTableRoutes = () => {
  const { t } = useTranslation();
  return (
    <DrawerStateProvider>
      <Routes>
        <Route
          index
          element={
            <>
              <PageHeader
                title={t('master-table-management')}
                breadcrumbs={[{ title: t('master-table-management') }]}
              />
              <PageContent>
                <MasterTableListPage />
              </PageContent>
            </>
          }
        />
        <Route path="policy/*" element={<PolicyRoutes />} />
        <Route
          path="/major-matter"
          element={
            <>
              <PageHeader
                title={t('major-matter-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('major-matter-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="major-matter" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <MajorMatterPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-item-major-matter"
          element={
            <>
              <PageHeader
                title={t('expenditure-item-major-matter-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('expenditure-item-major-matter-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-item-major-matter" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditureItemMajorMattersPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-purpose"
          element={
            <>
              <PageHeader
                title={t('expenditure-purposes-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('expenditure-purposes-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-purpose" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditurePurposesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-nature"
          element={
            <>
              <PageHeader
                title={t('expenditure-natures-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('expenditure-natures-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-nature" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditureNaturesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/revenue-nature"
          element={
            <>
              <PageHeader
                title={t('revenue-natures-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('revenue-natures-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="revenue-nature" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <RevenueNaturesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/accounting-type"
          element={
            <>
              <PageHeader
                title={t('accounting-types-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('accounting-types-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="accounting-type" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <AccountingTypesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/budget-type"
          element={
            <>
              <PageHeader
                title={t('budget-types-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('budget-types-page') },
                ]}
              />
              <PageContent>
                <BudgetTypesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-budget-purpose"
          element={
            <>
              <PageHeader
                title={t('expenditure-budget-purposes-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('expenditure-budget-purposes-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-budget-purpose" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditureBudgetPurposesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-budget-jurisdiction"
          element={
            <>
              <PageHeader
                title={t('expenditure-budget-jurisdictions-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('expenditure-budget-jurisdictions-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-budget-jurisdiction" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditureBudgetJurisdictionsPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-budget-organization"
          element={
            <>
              <PageHeader
                title={t('expenditure-budget-organizations-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('expenditure-budget-organizations-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-budget-organization" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditureBudgetOrganizationsPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/account-title"
          element={
            <>
              <PageHeader
                title={t('account-titles-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('account-titles-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="account-title" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <AccountTitlesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/cost"
          element={
            <>
              <PageHeader
                title={t('costs-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('costs-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="cost" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <CostsPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-purpose-of-settlement-statistics"
          element={
            <>
              <PageHeader
                title={t('expenditure-purposes-of-settlement-statistics-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  {
                    title: t(
                      'expenditure-purposes-of-settlement-statistics-page',
                    ),
                  },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-purpose-of-settlement-statistics-preset" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditurePurposeOfSettlementStatisticsPresetsPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/expenditure-item-nature"
          element={
            <>
              <PageHeader
                title={t('expenditure-item-natures-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('expenditure-item-natures-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="expenditure-item-nature-preset" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <ExpenditureItemNaturePresetsPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/ministry"
          element={
            <>
              <PageHeader
                title={t('ministries-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('ministries-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="ministry" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <MinistriesPage />
              </PageContent>
            </>
          }
        />
        <Route
          path="/indicator-measurements"
          element={
            <>
              <PageHeader
                title={t('indicator-measurements-page')}
                breadcrumbs={[
                  {
                    title: t('master-table-management'),
                    href: '/master-table',
                  },
                  { title: t('indicator-measurements-page') },
                ]}
                buttons={
                  <MasterTableResourceGuard onlyEdit>
                    <CreateMasterTableButton type="indicator-measurement" />
                  </MasterTableResourceGuard>
                }
              />
              <PageContent>
                <IndicatorMeasurementsPage />
              </PageContent>
            </>
          }
        />

        <Route path="/reserved-project" element={<ReservedProjectsPage />} />
      </Routes>
    </DrawerStateProvider>
  );
};
